import { getFontStretch } from '../text/text-helpers';

const LibraryFactoryInstance = require('./library').instance;
const LibraryFactory = require('./library').factory;

const EditorFacade = require('../facade/editor/editor');
const RestrictionsFacade = require('../facade/restrictions/restrictions');
const AppDecorate = require('../app/decorate');

const template = require('./library/font-template.hbs');
let fontsList = defaultFontsInfo(INKIVE.C.FONT_LIST);

module.exports = AppDecorate(require('./app/fonts'), {
  fontListLoaded: false,
  initialized: false,
  customFonts: {},
  failedFonts: {},

  initialize: function () {
    const deferred = $.Deferred();
    const textPromise = LibraryFactoryInstance.get('texts');
    const customFontsPromise = LibraryFactoryInstance.get('fonts');
    let memberPromise = null;

    if (!INKIVE.Helpers.isDesygnerProject()) {
      const memberLibraryFactoryInstance = new LibraryFactory('member');
      memberPromise = memberLibraryFactoryInstance.get('fonts');
    }

    $.when(customFontsPromise, textPromise, memberPromise)
      .then(_.bind(this._onDataLoaded, this, deferred));

    return deferred;
  },

  isLoadedFont: function (fontFamily) {
    return _.any(fontsList, function (font) {
      return font.family === fontFamily;
    });
  },

  refreshCustomFonts: function () {
    const deferred = $.Deferred();

    LibraryFactoryInstance.cacheClear('fonts');
    LibraryFactoryInstance.get('fonts').then(function (data) {
      const newFonts = _.filter(data, function (font) {
        return !_.any(fontsList, function (lf) {
          return lf.family === font.data.family;
        });
      });

      if (newFonts.length) {
        fontsList = _.union(
          ownFontsInfo(newFonts),
          fontsList
        );

        addCSSFonts(newFonts);
      }

      deferred.resolve();
    });

    return deferred;
  },

  _onDataLoaded: function (deferred, userFonts, userTexts, memberFonts) {
    if (memberFonts) userFonts = this.processMemberFonts(userFonts, memberFonts);

    fontsList = _.union(ownFontsInfo(userFonts), defaultFontsInfo(INKIVE.C.FONT_LIST, userTexts));
    textDataInfo(fontsList, userTexts);

    findFontFamily('Lato').imported_css = true;
    findFontFamily('Nunito').imported_css = true;
    updateNunitoData(findFontFamily('Nunito'));
    this.fontListLoaded = true;
    EditorFacade.trigger('fontlist.loaded', {});
    this.initialized = true;
    deferred.resolve();
  },

  processMemberFonts: function (userFonts, memberFonts) {
    return userFonts.concat(memberFonts);
  },

  getUsableFonts: function () {
    const templateFonts = _.chain([...document.querySelectorAll('#svgcontent [font-family]')])
      .map((el) => {
        const customFontId = el.getAttribute('custom-font-id') || '';

        return {
          family: (el.getAttribute('font-family') || '').replace(/^'(.+)'$/, '$1'),
          customId: customFontId
        };
      }).uniq().compact().value();

    _.each(fontsList, function (font) {
      font.templateFonts = !!(_.find(templateFonts, function (f) {
        return !font.customId && !f.customId && f.family === font.family;
      }));
    });

    return RestrictionsFacade.isFunctionalityAllowed('function_use_desygner_font')
      ? fontsList
      : _.filter(fontsList, function (fontData) {
        return fontData.customId || fontData.templateFonts;
      });
  },

  get: function (family, width) {
    const font = _.clone(family
      ? findFontFamily(family)
      : fontsList);

    if (typeof width !== 'undefined' && font && font.customId) {
      font.support = getCustomFontSupport(font, width);
    }

    return font;
  },

  getCustomFont: function (fontId, forceAdd) {
    if (this.customFonts[fontId]) {
      const font = fontsList.find(({ customId }) => customId === fontId);

      if (font && font.imported_css) {
        return this.customFonts[fontId];
      }
    }

    const deferred = $.Deferred();

    this.customFonts[fontId] = deferred.promise();

    LibraryFactoryInstance.getResource('fonts', fontId)
      .then(fontData => {
        if (!_.isArray(fontData)) {
          fontData = [fontData];
        }
        fontsList = _.union(fontsList, ownFontsInfo(fontData));
        addCSSFonts(fontData, forceAdd)
          .then(() => deferred.resolve())
          .catch((e) => deferred.resolve());
      }, () => deferred.reject(new Error(`Failed to get custom fonts with id ${fontId}`)));

    return this.customFonts[fontId];
  },

  getFontUrl: function (fontFamily, fontWeight, isItalic) {
    const pattern = /url\(.*?\)/g;
    const urls = [];
    const fontStyle = isItalic
      ? 'italic'
      : 'normal';

    if (fontFamily.split(' ').length > 1) {
      fontFamily = '"' + fontFamily + '"';
    }

    fontWeight = fontWeight || 400;

    _.each(document.styleSheets, function (styleSheet) {
      _.each(styleSheet.cssRules, function (cssRule) {
        if (cssRule instanceof CSSFontFaceRule) {
          const cssStyle = cssRule.style;
          let isValid = _.contains(cssStyle, 'font-family') && cssStyle.fontFamily === fontFamily;

          isValid = _.contains(cssStyle, 'font-weight')
            ? (parseInt(cssStyle.fontWeight) === fontWeight && isValid)
            : isValid;
          isValid = _.contains(cssStyle, 'font-style')
            ? (cssStyle.fontStyle === fontStyle && isValid)
            : isValid;

          isValid && urls.push(cssStyle.src.match(pattern));
        }
      });
    });

    return JSON.stringify(_.uniq(_.flatten(urls)));
  },

  addFontToList: function (fontFamily) {
    fontsList.push({
      family: fontFamily,
      support: {
        b: false,
        bi: false,
        i: false,
        variants: [{ variant: '400' }]
      }
    });
  },

  getSanitizedFontFamily: function (fontFamily) {
    const fontVariants = Object.keys(standardFontWeights);

    if (!this.get(fontFamily)) {
      const match = fontFamily.match(/^(.+)-([^-]+)$/);

      if (match && fontVariants.indexOf(match[2].toLowerCase()) > -1 && this.get(match[1])) {
        return match[1];
      }
    }

    return fontFamily;
  },

  addSystemFontCSS: function (fontFamily) {
    const font = this.get(fontFamily);

    if (font && font.isSystem && !font.imported) {
      font.imported = true;

      const variants = mapFontVariants(font.support);

      // create a style tag in head with class 'desygner-system-font-css'
      const styleContainer = document.querySelector('head>style.desygner-system-font-css');
      if (!styleContainer) {
        const style = document.createElement('style');
        style.className = 'desygner-system-font-css';
        document.head.appendChild(style);
      }

      $('head>style.desygner-system-font-css').append(template({
        family: font.family,
        variants
      }));
    }
  },

  importDesignCSSFonts: async function (texts, forceAdd) {
    const that = this;

    if (!this.initialized || !this.fontListLoaded) {
      setTimeout(() => { that.importDesignCSSFonts(texts); }, 5);

      return;
    }

    const desygnerFonts = [];

    if (!texts) {
      texts = [];
      [...document.querySelectorAll('#svgcontent [font-family]')]
        .map((element) => {
          return {
            family: element.getAttribute('font-family').replace(/['"]+/g, ''),
            size: element.getAttribute('font-size'),
            weight: element.getAttribute('font-weight') || 'normal',
            stretch: element.getAttribute('font-stretch') || 'normal',
            style: element.getAttribute('font-style') || 'normal',
            customId: Number(element.getAttribute('custom-font-id'))
          };
        }).forEach((font) => {
          texts.find(item => item.family === font.family) || texts.push(font);
        });
    } else if (!_.isArray(texts)) {
      texts = [texts];
    }

    for (const font of texts) {
      const _font = findFontFamily(font.family);
      if (_font && _font.customId) {
        font.customId = _font.customId;
      }

      if (font.customId) {
        try {
          await this.getCustomFont(font.customId, forceAdd);
        } catch (error) {
          await addCSSDesygnerFonts([_font]);
        }
      } else {
        if (_font && _font.family) _font.family = _font.family.replace(/'/g, '');
        desygnerFonts.push(_font);
      }
    }

    await addCSSDesygnerFonts(desygnerFonts);

    INKIVE.EditorFacade.trigger('design-font.loaded');
  }
});

function getCustomFontSupport (font, width) {
  width = getNumericWidth();

  return {
    b: font.support.b,
    bi: font.support.bi,
    i: _.contains(font.support.variants, width + 'italic'),
    variants: font.support.variants
  };

  function getNumericWidth () {
    switch (width) {
      case 'normal':
        return 400;
      case 'bold':
        return 700;
    }

    if (width === 'bolder') {
      const max = _.max(font.support.variants);
      return max > 700
        ? max
        : 800;
    }

    if (width === 'lighter') {
      const min = _.min(font.support.variants);
      return min < 400
        ? min
        : 300;
    }

    return width;
  }
}

function findFontFamily (family) {
  return _(fontsList).find(function (font) {
    return font.family === family ||
      '\'' + font.family.replace(/'/g, '\\\'') + '\'' === family ||
      font.family.replace(/'/g, '\\\'') === family;
  });
}

function addCSSFonts (fonts, forceAdd) {
  const fontsInDesign = [...new Set(
    [...document.querySelectorAll('[font-family]')]
      .map(el => el.getAttribute('font-family').replace(/['"]+/g, ''))
      .filter(el => el !== 'desygner-icons')
  )];

  return Promise.all(fonts.map(font => {
    if (font && font.data && font.data.family) {
      // Skip UI fonts; we don't allow users override those fonts
      if (['Lato', 'Nunito'].includes(font.data.family)) return true;

      if (forceAdd || (!font.data.is_system_font && !font.data.imported_css && fontsInDesign.includes(font.data.family))) {
        return createFontFace({
          ...font,
          data: mapFontData(font.data),
          variants: mapFontVariants(font.data)
        });
      }
    }

    return true;
  }));
}

function addCSSDesygnerFonts (fonts) {
  const $style = getStyleTag();
  const beginning = '<link class="desygner-font-source" crossorigin="anonymous" rel="stylesheet" type="text/css" href="//fonts.googleapis.com/';
  const end = '"/>\n';
  let variants = '';

  _.each(fonts, function (font) {
    if (!font || font.imported_css) {
      return;
    }

    const earlyAccess = font.earlyaccess;

    if (earlyAccess) {
      $style.append(beginning + '/earlyaccess/' + earlyAccess + '.css' + end);
    } else {
      variants += font.family + ':' + font.support.variants.map(({ variant }) => variant).join(',') + '|';
    }

    font.imported_css = true;
  });

  variants && $('head').prepend(beginning + 'css?family=' + variants + '&subset=latin-ext,cyrillic' + end);

  return Promise.all([...fonts].map(font => font?.family && waitFontLoad(font.family)));
}

function getStyleTag () {
  const $fontSources = $('#font-sources');

  if ($fontSources.length) return $fontSources;

  $('head').append($('<style id="font-sources" type="text/css" />'));

  return $('#font-sources');
}

function isFontLoaded (fontFamily) {
  // A bug that browser cannot check Lato
  if (['Lato', 'Nunito'].includes(fontFamily)) return true;
  return document.fonts.check(`1em ${fontFamily}`);
}

function waitFontLoad (fontFamily, retry = 5) {
  if (isFontLoaded(fontFamily)) return Promise.resolve();

  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (isFontLoaded(fontFamily)) {
        clearInterval(interval);
        resolve();
      } else if (retry === 0) {
        clearInterval(interval);
        resolve();
      } else {
        retry--;
      }
    }, 500);
  });
}

export function createFontTag (font) {
  const tagId = `font-source-${font.id}`;

  if (document.getElementById(tagId)) return;

  const fontTemplate = template({
    family: font.data.family,
    variants: font.variants
  });
  const tag = document.createElement('style');
  tag.id = `font-source-${font.id}`;
  tag.innerHTML = fontTemplate;

  document.head.appendChild(tag);
}

export async function loadFontFace (family, source) {
  const { weight, width, variant } = source;
  try {
    if (INKIVE.FontsFactory.failedFonts[`${family}-${variant}-${source.source}`]) return;
    const font = new FontFace(family, `url(${source.source})`, {
      style: variant.includes('italic') ? 'italic' : variant.includes('oblique') ? 'oblique' : 'normal',
      weight: weight || parseInt(variant),
      stretch: getFontStretch(width) || 'normal'
    });

    await font.load();
    document.fonts.add(font);

    INKIVE.FontsFactory.failedFonts[`${family}-${variant}-${source.source}`] = true;
  } catch (error) {
    INKIVE.FontsFactory.failedFonts[`${family}-${variant}-${source.source}`] = true;
    console.error(error);
  }
}

export function createFontFace (font) {
  return Promise.all(
    font.data.sources.map((source) => {
      return loadFontFace(font.data.family, source);
    })
  );
}

export function mapFontVariants (font) {
  const variants = {};
  const supportSources = font.support && font.support.sources;
  const sources = font.sources || supportSources;

  for (const source of sources) {
    const index = source.variant || '400';

    variants[index] = variants[index] || getInitialVariantInfo(source);
    variants[index].sources[source.type] = { ...source };
    const base64Source = removeProtocol(variants[index].sources[source.type].source);
    variants[index].sources[source.type].source = base64Source;
  }

  font.imported_css = true;

  return variants;
}

export function removeProtocol (url) {
  return url.replace(/http[s]?:/, '');
}
export function mapFontData (font) {
  const sources = font.sources
    ? font.sources.map((variant) => ({
      ...variant,
      source: removeProtocol(variant.source)
    }))
    : [];

  return {
    ...font,
    sources
  };
}

function getInitialVariantInfo (source) {
  const data = { sources: {} };

  if (source.variant) {
    if (source.variant.indexOf('italic') > -1) {
      data.style = 'italic';
    }

    data.weight = source.variant.indexOf('regular') > -1
      ? '400'
      : source.variant.replace('italic', '');
  }

  if (source.width) {
    switch (source.width.toLowerCase()) {
      case 'cond':
      case 'condensed':
        data.stretch = 'condensed';
        break;
      case 'comp':
      case 'compressed':
        data.stretch = 'ultra-condensed';
        break;
      default:
        data.stretch = source.width.toLowerCase();
    }
  }

  return data;
}

function ownFontsInfo (userFonts) {
  return _.compact(_.map(userFonts, function (font) {
    if (!font || !font.data || !font.data.family) {
      return;
    }

    const support = { b: false, bi: false, i: false };
    let options = {};
    font.data.sources
      .filter(source => {
        const ignoreTypes = ['woff2'];
        return !ignoreTypes.includes(source.type);
      }).forEach(function (source) {
        let variant = source.variant
          ? source.variant.replace('regular', '400')
          : '400';

        if (variant === 'italic') { variant = '400italic'; }

        defineSupport(variant, support);

        options = createVariant({
          ...source,
          variant
        }, options, font.data.family);
      });

    const variants = this.fontVariants = Object.values(Object.keys(options).sort().reduce(
      (obj, key) => {
        obj.push(...Object.values(options[key]).sort((a, b) => Number(a.name.length) - Number(b.name.length)));

        return obj;
      },
      []
    ));

    const isSystem = font.data.is_system_font || font.data.family.match(/^page.+font.+$/);
    const isEmbedded = font.data.is_embedded_font;

    support.variants = variants;
    support.sources = font.data.sources;

    return {
      family: font.data.family,
      name: font.data.name || font.data.family,
      support,
      customId: font.type === 'Font' && font.id,
      isSystem,
      isEmbedded,
      isDeleted: font.is_deleted
    };
  }));
}

function textDataInfo (fontList, userTexts) {
  _.each(userTexts, function (text) {
    const variant = { variant: text.data.weight + '' || '400' };

    findFontFamily(text.data.family) || fontList.push({
      family: text.data.family,
      name: text.data.name || text.data.family,
      support: {
        ...defineSupport(variant),
        variants: [variant]
      }
    });
  });
}

function defineSupport (variant, support = { b: false, bi: false, i: false }) {
  if (variant === '700') {
    support.b = true;
  }

  if (variant === '700italic' || variant === '700oblique') {
    support.bi = true;
  }

  if (variant === '400italic' || variant === '400oblique' || variant === 'italic' || variant === 'oblique') {
    support.i = true;
  }

  return support;
}

function defaultFontsInfo (defaultFonts, userTexts) {
  const usedFonts = {};

  userTexts && _.each(userTexts, function (text) {
    const family = text.data.family;
    usedFonts[family] = usedFonts[family] || 0;
    usedFonts[family]++;
  });

  const dataInfo = _.map(defaultFonts, function (support, family) {
    return {
      family,
      support: {
        ...support,
        variants: support.variants.map((variant) => ({ variant }))
      }
    };
  });

  return _.sortBy(dataInfo, function (data) {
    return -(usedFonts[data.family] || 0);
  });
}

function updateNunitoData (font) {
  Object.assign(font.support, {
    b: true,
    bi: true,
    i: true,
    variants: ['200', '300', '400', '500', '600', '700', '800', '900'].map((variant) => ({ variant }))
  });
}

function createVariant (fontVariant, fontFamilyOptions, family) {
  const fileDetails = fontVariant.source ? extractFromUrl(fontVariant.source) : { type: 'ttf', name: '', fullName: '' };

  const nameParts = [family];
  const variant = fontVariant.variant.substr(0, 3);
  const fontWeight = fontVariant.weight || variant;

  nameParts.push(fontWeight);

  const style = fontVariant.slope || fontVariant.variant.substr(3);
  nameParts.push(style);

  fontVariant.width && nameParts.push(fontVariant.width);

  const params = fontVariant.parameters && Object.keys(fontVariant.parameters).length && fontVariant.parameters;
  const fullName = params ? `${params.full_name} - ${fileDetails.fullName}` : fileDetails.fullName;

  params && params.hairline && nameParts.push(params.hairline);

  if (!fontFamilyOptions[fontWeight]) { fontFamilyOptions[fontWeight] = {}; }

  fontFamilyOptions[fontWeight][fileDetails.name] = {
    id: fontVariant.id,
    name: nameParts.join(' '),
    variant,
    style,
    weight: fontVariant.weight || variant,
    filename: fileDetails.name,
    tooltip: fullName
  };

  return fontFamilyOptions;
}

function extractFromUrl (src) {
  const lastItem = src.split('/').slice(-1)[0];
  const parts = lastItem.split('.');

  return {
    name: parts.slice(0, -1).join('.'),
    type: parts.slice(-1)[0],
    fullName: lastItem
  };
}

// source: https://learn.microsoft.com/en-us/dotnet/api/system.windows.fontweights?view=windowsdesktop-8.0
const standardFontWeights = {
  thin: 100,
  extralight: 200,
  ultralight: 200,
  light: 300,
  book: 400,
  normal: 400,
  regular: 400,
  roman: 400,
  medium: 500,
  semibold: 600,
  demibold: 600,
  bold: 700,
  extrabold: 800,
  ultrabold: 800,
  black: 900,
  heavy: 900,
  ultrablack: 900,
  extrablack: 950
};
